import React from 'react';
import { Check } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { MAT, SLOTS, PRESETS, FACADES } from './catalog';

const swatchBg = (id) => {
  const m = MAT[id];
  if (!m) return {};
  if (m.kind === 'wood' || m.kind === 'floor-wood') return { background: `repeating-linear-gradient(92deg, ${m.c} 0 5px, ${m.c2} 5px 6px, ${m.c} 6px 11px)` };
  if (m.kind === 'marble') return { background: `linear-gradient(135deg, ${m.c} 0 40%, ${m.c2} 42%, ${m.c} 45% 70%, ${m.c2} 71%, ${m.c} 73%)` };
  if (m.kind === 'metal') return { background: `linear-gradient(135deg, ${m.c}, ${m.c2} 50%, ${m.c})` };
  return { background: m.c };
};

const FACADE_ICON = {
  lisse: <><rect x="4" y="4" width="32" height="40" /><path d="M30 18v12" strokeWidth="2.5" /></>,
  cannele: <><rect x="4" y="4" width="32" height="40" />{[9, 14, 19, 24, 29].map((x) => <path key={x} d={`M${x + 1} 6v36`} opacity="0.6" />)}</>,
  cadre: <><rect x="4" y="4" width="32" height="40" /><rect x="10" y="10" width="20" height="28" /></>,
  filet: <><rect x="4" y="4" width="32" height="40" /><rect x="7" y="7" width="26" height="34" opacity="0.7" /></>,
  gorge: <><rect x="4" y="9" width="32" height="35" /><path d="M4 5.5h32" strokeWidth="2.5" /></>,
  verre: <><rect x="4" y="4" width="32" height="40" strokeWidth="2.5" /><rect x="8" y="8" width="24" height="32" fill="currentColor" opacity="0.18" stroke="none" /></>,
};

function FacadePicker({ value, onChange }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3 gap-3">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966]">Collection de façades</p>
        <p className="text-xs text-[#d9c3a0] truncate" data-testid="facade-current">{FACADES[value]?.label}</p>
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="facade-picker">
        {Object.entries(FACADES).map(([k, f]) => (
          <button key={k} type="button" onClick={() => onChange(k)} data-testid={`facade-${k}`}
            className={`flex flex-col items-center gap-1.5 py-3 px-1 border transition-colors duration-200 ${value === k ? 'border-[#a68966] bg-[#a68966]/15 text-[#e6d3b3]' : 'border-white/10 text-gray-400 hover:border-white/30'}`}>
            <svg viewBox="0 0 40 48" className="w-8 h-10" fill="none" stroke="currentColor" strokeWidth="1.5">{FACADE_ICON[k]}</svg>
            <span className="font-playfair text-sm leading-none">{f.label}</span>
            <span className="text-[9px] text-gray-500 leading-tight text-center">{f.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function MaterialsPanel({ cfg, update }) {
  const setMat = (slot, id) => update((c) => ({ ...c, mats: { ...c.mats, [slot]: id } }));
  return (
    <div className="space-y-8">
      <FacadePicker value={cfg.facade || 'lisse'} onChange={(f) => update((c) => ({ ...c, facade: f }))} />
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966] mb-3">Ambiances signature</p>
        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map((pr) => (
            <button key={pr.id} type="button" onClick={() => update((c) => ({ ...c, facade: pr.facade || c.facade, mats: { ...c.mats, ...pr.mats } }))} data-testid={`preset-${pr.id}`}
              className="group border border-white/10 hover:border-[#a68966] p-3 text-left transition-colors duration-200">
              <div className="flex -space-x-1.5 mb-2">
                {['front', 'top', 'metal', 'floor'].map((s) => <span key={s} className="w-6 h-6 rounded-full ring-2 ring-[#151515]" style={swatchBg(pr.mats[s])} />)}
              </div>
              <span className="text-xs text-gray-300 group-hover:text-[#e6d3b3]">{pr.label}</span>
            </button>
          ))}
        </div>
      </div>
      {SLOTS.map((slot) => (
        <div key={slot.id}>
          <div className="flex items-baseline justify-between mb-3 gap-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500">{slot.label}</p>
            <p className="text-xs text-[#d9c3a0] truncate" data-testid={`material-current-${slot.id}`}>{cfg.mats[slot.id] === 'same' ? 'Identique aux façades' : MAT[cfg.mats[slot.id]]?.label}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {slot.mats.map((id) => {
              const on = cfg.mats[slot.id] === id;
              return (
                <button key={id} type="button" title={id === 'same' ? 'Identique aux façades' : MAT[id].label} onClick={() => setMat(slot.id, id)} data-testid={`swatch-${slot.id}-${id}`}
                  className={`relative w-10 h-10 rounded-full transition-transform duration-200 hover:scale-110 ${on ? 'ring-2 ring-offset-2 ring-offset-[#151515] ring-[#a68966]' : 'ring-1 ring-white/10'}`}
                  style={id === 'same' ? { background: 'conic-gradient(#5c3b25 0 50%, #ebe7df 0)' } : swatchBg(id)}>
                  {on && <Check className="absolute inset-0 m-auto w-4 h-4 text-white mix-blend-difference" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between border border-white/10 p-4">
        <div>
          <p className="text-sm text-gray-200">Éclairage LED intégré</p>
          <p className="text-xs text-gray-500">Rubans chauds sous étagères & plafond</p>
        </div>
        <Switch checked={cfg.led} onCheckedChange={(v) => update((c) => ({ ...c, led: v }))} data-testid="led-toggle" />
      </div>
    </div>
  );
}

const Range = ({ label, value, min, max, onChange, id }) => (
  <div>
    <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3"><span>{label}</span><span className="text-[#d9c3a0] text-xs tracking-normal">{Math.round(value / 10)} cm</span></div>
    <Slider value={[value]} min={min} max={max} step={50} onValueChange={([v]) => onChange(v)} data-testid={id} />
  </div>
);

export function SpacePanel({ cfg, L, update, onReset }) {
  const setRoom = (k, v) => update((c) => ({ ...c, room: { ...c.room, [k]: v } }));
  return (
    <div className="space-y-7">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966]">Implantation</p>
      <div className="grid grid-cols-3 gap-2" data-testid="layout-chooser">
        {[['I', 'Linéaire', 'M6 8h28'], ['L', 'En L', 'M6 8h28M6 8v24'], ['U', 'En U', 'M6 32V8h28v24']].map(([k, label, d]) => {
          const on = (cfg.layout || 'I') === k;
          return (
            <button key={k} type="button" onClick={() => update((c) => ({ ...c, layout: k, modulesB: c.modulesB || [], modulesC: c.modulesC || [] }))} data-testid={`layout-${k}`}
              className={`flex flex-col items-center gap-2 py-3 border transition-colors ${on ? 'border-[#a68966] bg-[#a68966]/15 text-[#e6d3b3]' : 'border-white/10 text-gray-400 hover:border-white/30'}`}>
              <svg viewBox="0 0 40 40" className="w-9 h-9"><path d={d} fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="square" /></svg>
              <span className="text-[10px] uppercase tracking-[0.2em]">{label}</span>
            </button>
          );
        })}
      </div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966]">Dimensions de la pièce</p>
      <Range label="Largeur du mur" value={L.W} min={Math.max(1800, Math.ceil(L.run / 50) * 50)} max={9000} onChange={(v) => setRoom('w', v)} id="room-width-slider" />
      <Range label="Hauteur sous plafond" value={cfg.room.h} min={2300} max={3400} onChange={(v) => setRoom('h', v)} id="room-height-slider" />
      <Range label="Profondeur de la pièce" value={L.D} min={1800} max={8000} onChange={(v) => setRoom('d', v)} id="room-depth-slider" />
      <p className="text-xs text-gray-500 leading-relaxed">Les éléments sont centrés sur le mur principal. La largeur minimale s'adapte automatiquement à votre composition ({Math.round(L.run / 10)} cm).</p>
      <button type="button" onClick={onReset} data-testid="reset-config-btn" className="text-xs uppercase tracking-[0.25em] text-gray-400 hover:text-[#a68966] border-b border-white/20 hover:border-[#a68966] pb-1 transition-colors">
        Réinitialiser cette rubrique
      </button>
    </div>
  );
}
